import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import PageLayout from '@/components/feature/PageLayout';
import ClientLogosStrip from '@/components/feature/ClientLogosStrip';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

/* ── Industry data for the 6 dedicated landing pages ── */
const industryCards = [
  {
    id: 'saas',
    name: 'SaaS',
    tagline: 'Software as a Service',
    icon: 'ri-apps-line',
    link: '/industries/saas',
    accentColor: 'bg-primary-500',
    accentBg: 'bg-primary-50',
    accentBorder: 'border-primary-200',
    accentText: 'text-primary-700',
    barColor: 'bg-primary-500',
    stats: { engineers: 86, experience: '6.2 yrs', matchTime: '48h' },
    trustBadges: ['SOC 2 Ready', 'ISO 27001', 'GDPR'],
    topRoles: ['Platform Engineers', 'Full-Stack Devs', 'DevOps', 'Data Engineers'],
    description: 'Build and scale multi-tenant platforms, implement usage-based billing, and ship features faster with engineers who have built B2B SaaS from seed to Series C.',
    keyTech: ['React', 'Node.js', 'Kubernetes', 'AWS', 'Stripe'],
    heroImg: 'https://readdy.ai/api/search-image?query=Modern%20SaaS%20dashboard%20interface%20with%20analytics%20charts%20and%20user%20management%20panels%2C%20clean%20navy%20blue%20and%20teal%20UI%20on%20dark%20background%2C%20professional%20software%20product%20render%2C%20minimal%20elegant%20design&width=800&height=480&seq=hub-saas-1&orientation=landscape',
  },
  {
    id: 'fintech',
    name: 'Fintech',
    tagline: 'Banking & Financial Services',
    icon: 'ri-bank-line',
    link: '/industries/fintech',
    accentColor: 'bg-amber-500',
    accentBg: 'bg-amber-50',
    accentBorder: 'border-amber-200',
    accentText: 'text-amber-700',
    barColor: 'bg-amber-500',
    stats: { engineers: 54, experience: '7.1 yrs', matchTime: '48h' },
    trustBadges: ['PCI-DSS Vetted', 'SOC 2 Type II', 'Bank-Grade'],
    topRoles: ['Core Banking Devs', 'Quant Engineers', 'Security Devs', 'Risk Analysts'],
    description: 'Build real-time payment systems, core banking infrastructure, and regulatory-compliant platforms with engineers who understand PCI-DSS, SOX, and millisecond latency.',
    keyTech: ['Java', 'Python', 'Kafka', 'PostgreSQL', 'HSM'],
    heroImg: 'https://readdy.ai/api/search-image?query=Fintech%20payment%20platform%20interface%20showing%20real-time%20transaction%20flows%20and%20financial%20charts%2C%20dark%20amber%20and%20navy%20UI%20design%2C%20professional%20digital%20banking%20interface%20render%2C%20secure%20and%20modern%20aesthetic&width=800&height=480&seq=hub-fintech-1&orientation=landscape',
  },
  {
    id: 'healthtech',
    name: 'Healthtech',
    tagline: 'Healthcare & Pharma',
    icon: 'ri-heart-pulse-line',
    link: '/industries/healthtech',
    accentColor: 'bg-emerald-500',
    accentBg: 'bg-emerald-50',
    accentBorder: 'border-emerald-200',
    accentText: 'text-emerald-700',
    barColor: 'bg-emerald-500',
    stats: { engineers: 34, experience: '5.9 yrs', matchTime: '48h' },
    trustBadges: ['HIPAA Certified', 'HITRUST CSF', 'FDA 21 CFR'],
    topRoles: ['FHIR Engineers', 'EHR Integration', 'Telemedicine Devs', 'Cloud Architects'],
    description: 'Build HIPAA-compliant platforms, EHR integrations, and telemedicine infrastructure with engineers who understand FHIR, HL7, and the regulatory healthcare landscape.',
    keyTech: ['FHIR R4', 'HL7', 'Python', 'AWS HIPAA', 'React'],
    heroImg: 'https://readdy.ai/api/search-image?query=Telemedicine%20platform%20interface%20showing%20patient%20vitals%20and%20video%20consultation%20dashboard%2C%20clean%20teal%20and%20white%20healthcare%20UI%20design%2C%20modern%20digital%20health%20application%20render%2C%20minimal%20professional%20aesthetic&width=800&height=480&seq=hub-health-1&orientation=landscape',
  },
  {
    id: 'ecommerce',
    name: 'E-commerce',
    tagline: 'Retail & Commerce Platforms',
    icon: 'ri-shopping-cart-line',
    link: '/industries/ecommerce',
    accentColor: 'bg-rose-500',
    accentBg: 'bg-rose-50',
    accentBorder: 'border-rose-200',
    accentText: 'text-rose-700',
    barColor: 'bg-rose-500',
    stats: { engineers: 42, experience: '5.8 yrs', matchTime: '48h' },
    trustBadges: ['PCI-DSS Compliant', 'GDPR Ready', 'Stripe Certified'],
    topRoles: ['Platform Engineers', 'Commerce Devs', 'ML Engineers', 'Mobile Devs'],
    description: 'Scale omnichannel platforms, optimize conversion funnels, and build recommendation engines with engineers who have shipped code during Black Friday traffic surges.',
    keyTech: ['Next.js', 'Shopify', 'GraphQL', 'Redis', 'React Native'],
    heroImg: 'https://readdy.ai/api/search-image?query=E-commerce%20platform%20admin%20dashboard%20showing%20product%20catalog%20and%20sales%20analytics%2C%20clean%20rose%20and%20white%20UI%20design%2C%20professional%20retail%20software%20interface%20render%2C%20modern%20minimal%20aesthetic&width=800&height=480&seq=hub-ecomm-1&orientation=landscape',
  },
  {
    id: 'logistics',
    name: 'Logistics',
    tagline: 'Supply Chain & Fleet Management',
    icon: 'ri-truck-line',
    link: '/industries/logistics',
    accentColor: 'bg-yellow-500',
    accentBg: 'bg-yellow-50',
    accentBorder: 'border-yellow-200',
    accentText: 'text-yellow-700',
    barColor: 'bg-yellow-500',
    stats: { engineers: 22, experience: '5.1 yrs', matchTime: '48h' },
    trustBadges: ['ISO 27001', 'SOC 2 Ready', 'GDPR Compliant'],
    topRoles: ['Route Optimization', 'Fleet Mgmt Devs', 'WMS Integration', 'Data Engineers'],
    description: 'Build fleet management platforms, warehouse automation systems, and AI-powered route optimization with engineers who have reduced delivery costs for global networks.',
    keyTech: ['Python', 'OSRM', 'Kafka', 'MQTT', 'React Native'],
    heroImg: 'https://readdy.ai/api/search-image?query=Logistics%20fleet%20management%20dashboard%20showing%20route%20optimization%20map%20and%20delivery%20tracking%2C%20dark%20navy%20and%20amber%20UI%20design%2C%20professional%20supply%20chain%20software%20interface%2C%20modern%20minimal%20aesthetic&width=800&height=480&seq=hub-logistics-1&orientation=landscape',
  },
  {
    id: 'edtech',
    name: 'EdTech',
    tagline: 'Learning Platforms & Education',
    icon: 'ri-graduation-cap-line',
    link: '/industries/edtech',
    accentColor: 'bg-pink-500',
    accentBg: 'bg-pink-50',
    accentBorder: 'border-pink-200',
    accentText: 'text-pink-700',
    barColor: 'bg-pink-500',
    stats: { engineers: 28, experience: '4.9 yrs', matchTime: '48h' },
    trustBadges: ['FERPA Compliant', 'COPPA Certified', 'WCAG 2.1 AA'],
    topRoles: ['Adaptive Learning', 'WebRTC Engineers', 'LMS Integration', 'Accessibility Devs'],
    description: 'Build adaptive learning systems, LMS integrations, and real-time classroom tools with engineers who understand pedagogy, accessibility, and student engagement at scale.',
    keyTech: ['WebRTC', 'LTI 1.3', 'SCORM', 'React', 'Python'],
    heroImg: 'https://readdy.ai/api/search-image?query=EdTech%20learning%20platform%20showing%20interactive%20classroom%20interface%20and%20student%20progress%20dashboards%2C%20clean%20pink%20and%20white%20UI%20design%2C%20modern%20educational%20software%20application%20render%2C%20minimal%20professional%20aesthetic&width=800&height=480&seq=hub-edtech-1&orientation=landscape',
  },
];

const overallStats = [
  { icon: 'ri-building-2-line', value: '6', label: 'Dedicated Industry Hubs', accent: 'text-primary-400' },
  { icon: 'ri-user-star-line', value: '266+', label: 'Industry-Vetted Engineers', accent: 'text-amber-400' },
  { icon: 'ri-shield-check-line', value: '98%', label: 'First-Match Success Rate', accent: 'text-emerald-400' },
  { icon: 'ri-time-line', value: '48h', label: 'Avg. Talent Match Time', accent: 'text-rose-400' },
];

/* ── Hero ── */
function PageHero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-28 pb-20 md:pt-40 md:pb-0 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20corporate%20background%20with%20multi-industry%20icons%20representing%20healthcare%20fintech%20logistics%20education%20ecommerce%20and%20SaaS%20floating%20as%20glowing%20symbols%20in%20deep%20space%2C%20deep%20navy%20blue%20with%20amber%20gold%20and%20teal%20green%20accent%20lights%2C%20professional%20minimal%20composition%20suitable%20for%20website%20hero%20banner%20with%20white%20text%20overlay%2C%20global%20industry%20expertise%20aesthetic&width=1600&height=900&seq=industries-hub-hero-1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/60 via-navy-900/70 to-navy-900/90"></div>
      </div>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[300px] bg-emerald-500/5 rounded-full blur-3xl"></div>
      </div>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

     

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-4xl mx-auto pb-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-global-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Industries We Serve</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Domain Expertise
            <br />
            <span className="text-primary-400">Across 6 Industries</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-2xl mx-auto"
          >
            Every industry has its own compliance requirements, technology stack, and talent DNA. Explore our 6 dedicated hubs — each with vetted engineers, trust badges, and domain-matched screening.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-8 md:mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-3xl mx-auto"
          >
            {overallStats.map((stat, idx) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.4 + idx * 0.06, ...springTransition }}
                className="flex items-center gap-2.5 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-xl border bg-white/10 border-white/20"
              >
                <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0">
                  <i className={`${stat.icon} text-base md:text-lg ${stat.accent}`}></i>
                </div>
                <div className="text-left">
                  <div className="text-sm md:text-base font-bold leading-tight text-white">{stat.value}</div>
                  <div className="text-[10px] md:text-xs font-medium text-white/60">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
         <ClientLogosStrip />
      </div>
    </div>
  );
}

/* ── Industry Hub Card ── */
function IndustryHubCard({ industry, index }: { industry: typeof industryCards[0]; index: number }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40, scale: 0.97 }}
      animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ delay: index * 0.07, ...springTransition }}
      className="group bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-2xl hover:shadow-navy-900/5 transition-all duration-500 overflow-hidden flex flex-col"
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden bg-gray-50">
        <img
          src={industry.heroImg}
          alt={`${industry.name} engineering platform`}
          className={`w-full h-full object-cover group-hover:scale-105 transition-all duration-700 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImgLoaded(true)}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/50 via-transparent to-transparent"></div>
        {/* Top accent bar */}
        <div className={`absolute top-0 left-0 right-0 h-1 ${industry.accentColor}`}></div>
        {/* Industry badge */}
        <div className="absolute top-3 left-3">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold ${industry.accentBg} ${industry.accentText} border ${industry.accentBorder}`}>
            <i className={`${industry.icon} text-sm`}></i>
            {industry.name}
          </span>
        </div>
        {/* Engineers count badge */}
        <div className="absolute bottom-3 right-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-lg border border-white/20">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span className="text-xs font-bold text-navy-900">{industry.stats.engineers} available</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1">
        {/* Header */}
        <div className="mb-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-xl font-bold text-navy-900 group-hover:text-primary-700 transition-colors">{industry.name}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{industry.tagline}</p>
            </div>
            <div className={`w-10 h-10 flex items-center justify-center ${industry.accentColor} rounded-xl flex-shrink-0`}>
              <i className={`${industry.icon} text-lg text-white`}></i>
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">{industry.description}</p>

        {/* Quick stats row */}
        <div className={`flex items-center gap-4 py-3 px-4 rounded-xl ${industry.accentBg} border ${industry.accentBorder} mb-4`}>
          <div className="text-center">
            <div className={`text-lg font-bold ${industry.accentText}`}>{industry.stats.engineers}</div>
            <div className="text-[10px] text-gray-500">Engineers</div>
          </div>
          <div className="w-px h-8 bg-gray-200"></div>
          <div className="text-center">
            <div className={`text-lg font-bold ${industry.accentText}`}>{industry.stats.experience}</div>
            <div className="text-[10px] text-gray-500">Avg. Exp.</div>
          </div>
          <div className="w-px h-8 bg-gray-200"></div>
          <div className="text-center">
            <div className={`text-lg font-bold ${industry.accentText}`}>{industry.stats.matchTime}</div>
            <div className="text-[10px] text-gray-500">Match Time</div>
          </div>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {industry.trustBadges.map((badge) => (
            <span
              key={badge}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-navy-900/5 rounded-md text-[10px] font-semibold text-navy-900/70"
            >
              <i className="ri-shield-check-line text-emerald-500 text-[10px]"></i>
              {badge}
            </span>
          ))}
        </div>

        {/* Key tech */}
        <div className="flex flex-wrap gap-1.5 mb-5">
          {industry.keyTech.map((tech) => (
            <span
              key={tech}
              className="px-2 py-0.5 bg-gray-50 border border-gray-200 rounded-md text-[11px] font-medium text-gray-600"
            >
              {tech}
            </span>
          ))}
        </div>

        {/* CTA */}
        <Link
          to={industry.link}
          className={`flex items-center justify-center gap-2 w-full py-3 ${industry.accentColor} text-white text-sm font-bold rounded-xl hover:brightness-110 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap group/btn mt-auto`}
        >
          Explore {industry.name} Talent
          <i className="ri-arrow-right-line group-hover/btn:translate-x-1 transition-transform duration-300"></i>
        </Link>
      </div>
    </motion.div>
  );
}

/* ── Hub Grid Section ── */
function IndustryHubGrid() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.03 });

  return (
    <section className="py-16 md:py-24 bg-gray-50 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-primary-100/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-amber-50/40 rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-stack-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Dedicated Industry Hubs</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Choose Your
            <span className="text-primary-600"> Industry</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Each hub features domain-matched engineers, compliance-specific vetting, industry trust badges, real case studies, and FAQ sections. Click any card to explore the full hub.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
          {industryCards.map((industry, index) => (
            <IndustryHubCard key={industry.id} industry={industry} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Why Domain-Specific Matters ── */
function WhyDomainSpecific() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const points = [
    {
      icon: 'ri-shield-check-line',
      title: 'Compliance From Day One',
      desc: 'Every industry operates under different regulatory regimes. Our engineers arrive already familiar with HIPAA, PCI-DSS, FERPA, and SOC 2 — not learning on your dime.',
      stat: '12 compliance frameworks',
    },
    {
      icon: 'ri-code-s-slash-line',
      title: 'Domain Tech Stack Fluency',
      desc: 'SaaS engineers know multi-tenancy. Fintech engineers know Kafka and HSM. Healthcare engineers know FHIR R4. We match on specific domain experience, not just language.',
      stat: '6 specialized stacks',
    },
    {
      icon: 'ri-time-line',
      title: 'Faster Ramp-Up',
      desc: 'Engineers with prior domain experience contribute to architecture decisions on day one. No crash course on your industry required — they already speak the language.',
      stat: '2–3 day ramp-up',
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Measurable Outcomes',
      desc: 'Domain-matched talent reduces critical bugs, accelerates compliance certifications, and ships features faster. The numbers prove it across every industry we serve.',
      stat: '94% match success rate',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-award-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Why Domain-Specific Hiring Wins</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Generalists Learn.
            <span className="text-primary-600"> Specialists Ship.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            A great React developer is not automatically a great healthcare engineer. We match on domain depth — not just technical skill.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {points.map((p, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${p.icon} text-xl text-white`}></i>
              </div>
              <div className="mb-3 text-sm font-bold text-primary-600">{p.stat}</div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{p.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── All Industries Comparison Strip ── */
function IndustriesComparisonStrip() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <section className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4">
            <i className="ri-bar-chart-2-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Industry at a Glance</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4">
            Talent Pool by
            <span className="text-primary-400"> Industry</span>
          </h2>
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto">
            Real-time availability across all 6 industry hubs. Click any row to explore the full landing page.
          </p>
        </motion.div>

        <div className="space-y-3 md:space-y-4">
          {industryCards.map((ind, index) => (
            <motion.div
              key={ind.id}
              initial={{ opacity: 0, x: -30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.2 + index * 0.07, ...springTransition }}
            >
              <Link
                to={ind.link}
                className="flex items-center gap-4 md:gap-6 bg-white/5 hover:bg-white/10 rounded-2xl px-4 md:px-6 py-4 border border-white/10 hover:border-white/20 transition-all duration-300 cursor-pointer group"
              >
                <div className={`w-10 h-10 flex items-center justify-center ${ind.accentColor} rounded-xl flex-shrink-0`}>
                  <i className={`${ind.icon} text-lg text-white`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-bold text-white">{ind.name}</span>
                    <span className="text-xs text-white/50 hidden sm:block">{ind.tagline}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {ind.trustBadges.slice(0, 2).map((b) => (
                      <span key={b} className="text-[10px] text-white/50 font-medium">{b}</span>
                    ))}
                  </div>
                </div>
                {/* Progress bar representing engineers */}
                <div className="hidden md:flex flex-col gap-1 w-48">
                  <div className="flex justify-between text-[10px] text-white/50">
                    <span>{ind.stats.engineers} engineers</span>
                    <span>{ind.stats.experience} avg</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={inView ? { width: `${Math.min((ind.stats.engineers / 90) * 100, 100)}%` } : {}}
                      transition={{ delay: 0.3 + index * 0.07, duration: 0.8, ease: 'easeOut' }}
                      className={`h-full rounded-full ${ind.barColor}`}
                    ></motion.div>
                  </div>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                  <span className={`hidden sm:block text-xs font-bold px-2.5 py-1 rounded-lg ${ind.accentBg} ${ind.accentText}`}>
                    {ind.stats.matchTime}
                  </span>
                  <i className="ri-arrow-right-line text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all duration-300"></i>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── CTA ── */
function IndustriesCTA() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <section className="py-16 md:py-24 bg-gray-50 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 right-0 w-[400px] h-[400px] bg-primary-100/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 left-0 w-[400px] h-[400px] bg-amber-50/30 rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-6 shadow-sm">
            <i className="ri-briefcase-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Your Industry Not Listed?</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6">
            We Hire Across
            <span className="text-primary-600"> All Verticals</span>
          </h2>
          <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto mb-8 md:mb-10">
            Our 6 industry hubs cover the most regulated and specialized sectors. But our talent network spans every vertical — from media to real estate to defense. Tell us what you need.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/talent"
              className="px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Browse All Talent
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/case-studies"
              className="px-8 py-4 border-2 border-navy-900/20 text-navy-900 text-base font-semibold rounded-xl hover:bg-navy-900 hover:text-white hover:border-navy-900 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              View Case Studies
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Page ── */
export default function IndustriesPage() {
  return (
    <PageLayout
      title="Industries We Serve"
      description="Explore Thoughtic Solutions' 6 dedicated industry hubs — SaaS, Fintech, Healthtech, E-commerce, Logistics, and EdTech. Domain-vetted engineers, compliance expertise, and 48-hour talent matching."
      keywords="SaaS engineers, fintech developers, healthtech HIPAA, ecommerce developers, logistics supply chain engineers, EdTech FERPA, industry-specific remote talent, domain expertise engineering"
    >
      <PageHero />
      <IndustryHubGrid />
      <WhyDomainSpecific />
      <IndustriesComparisonStrip />
      <IndustriesCTA />
    </PageLayout>
  );
}